/**
 * User-scoped browser storage (WEBD-03).
 *
 * localStorage keys that hold one user's data or selections — tax settings,
 * account filters, watchlist/rebalancer selections, once-per-day guards,
 * optimizer inputs, search history. They are cleared whenever a session
 * ends or a different user signs in, so the next user starts clean.
 *
 * Device-level UI preferences (theme, sidebar, table columns/sorting,
 * calculator display modes) are deliberately NOT listed: they describe the
 * device, not the user's portfolio.
 *
 * The IndexedDB caches (option chains, dividend info — core/data) are kept
 * too: they hold public market data only, keyed by symbol, never user data.
 */

/** Exact localStorage keys owned by the signed-in user. */
export const USER_SCOPED_STORAGE_KEYS: readonly string[] = [
  'tax-page-settings',
  'gains-page-settings',
  'income-page-settings',
  'performance-page-settings',
  'performance-chart-settings',
  'performance-benchmark-symbols',
  'lastExpiredOptionsCleanup',
  'luminaria:dailySyncTabClaim',
  'watchlist.activeListId',
  'optionsLab.optimizer.formData',
  'market-stock-search-history',
  'market-stock-search-symbol',
];

/** localStorage key prefixes owned by the signed-in user (per-portfolio keys). */
export const USER_SCOPED_STORAGE_PREFIXES: readonly string[] = [
  'rebalancer-selected-accounts-',
];

/** Last signed-in user id; not user-scoped itself — it's how a user swap is detected. */
export const LAST_USER_ID_KEY = 'luminaria:lastUserId';

/** Remove every user-scoped localStorage entry. Storage failures are ignored. */
export function clearUserScopedStorage(): void {
  try {
    for (const key of USER_SCOPED_STORAGE_KEYS) {
      localStorage.removeItem(key);
    }
    const prefixed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && USER_SCOPED_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix))) {
        prefixed.push(key);
      }
    }
    for (const key of prefixed) {
      localStorage.removeItem(key);
    }
  } catch {
    // Storage unavailable (private mode / quota) — nothing to clear
  }
}

export function readLastUserId(): string | null {
  try {
    return localStorage.getItem(LAST_USER_ID_KEY);
  } catch {
    return null;
  }
}

export function writeLastUserId(userId: string): void {
  try {
    localStorage.setItem(LAST_USER_ID_KEY, userId);
  } catch {
    // Storage unavailable — swap detection falls back to the in-memory cache clear on sign-out
  }
}
